/**
 * Types and interfaces for KYC Upload System
 * Centralized type definitions used across all KYC provider components
 */

import { DocumentTypeEnum } from './document-kyc-upload-modal.component';

// ==================== FILE TYPES ====================

export type SupportedFileType = 'image' | 'pdf';

export interface FileInfo {
  file: File;
  type: SupportedFileType;
  preview?: string; // Data URL for preview
  size: number;
  name: string;
}

// ==================== VALIDATION ====================

export interface FileValidationRule {
  rule: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface FileValidationResult {
  valid: boolean;
  errors: FileValidationRule[];
  warnings: FileValidationRule[];
}

export interface ValidationConfig {
  maxFileSizeMB: number;
  allowedImageTypes: string[];
  allowedPdfTypes: string[];
  requireBothSides?: boolean; // For non-passport documents
}

// ==================== ERROR HANDLING ====================

export type KycErrorCode =
  | 'FILE_TOO_LARGE'
  | 'FILE_INVALID_TYPE'
  | 'FILE_CORRUPTED'
  | 'IMAGE_RESOLUTION_TOO_LOW'
  | 'UPLOAD_FAILED'
  | 'UPLOAD_TIMEOUT'
  | 'NETWORK_ERROR'
  | 'SERVER_ERROR'
  | 'PROVIDER_ERROR'
  | 'PROCESSING_TIMEOUT'
  | 'DOCUMENT_REJECTED'
  | 'DOCUMENT_UNCLEAR'
  | 'DOCUMENT_EXPIRED'
  | 'VALIDATION_FAILED'
  | 'RETRY_LIMIT_EXCEEDED'
  | 'USER_BLOCKED'
  | 'LEGAL_NAME_MISMATCH'
  | 'UNKNOWN_ERROR';

export interface KycError {
  code: KycErrorCode;
  message: string;
  technicalDetails?: string;
  suggestedAction: string;
  canRetry: boolean;
  retryable?: boolean;
  // Additional properties from API response
  shouldWait?: boolean;
  type?: string;
  actionRequired?: string;
  userMessage?: string;
  requestId?: string;
  data?: any;
  // Retry limit information
  currentRetries?: number;
  maxRetries?: number;
  userRole?: string;
  // Modal opening flag for specific errors
  requiresModal?: boolean;
  validationType?: string;
}

// ==================== PROCESSING STATUS ====================

export type ProcessingStep =
  | 'UPLOADING'
  | 'SUBMITTING'
  // SomosKycV1-only pre-submit phases (guided-capture read + user confirmation) — never present
  // in the other providers' step lists (see PROCESSING_STEPS below and somos-kyc-steps.ts).
  | 'READING'
  | 'CONFIRMING'
  | 'PRE_PROCESSING'
  | 'PROCESSING';

export interface ProcessingStatus {
  currentStep: ProcessingStep;
  message: string;
  progress?: number; // 0-100 if determinable
  isIndeterminate: boolean;
}

// The generic 4-step list rendered by every provider EXCEPT SomosKycV1 (which builds its own
// 5-step model from somos-kyc-steps.ts). The SomosV1-only keys are deliberately excluded so
// Object.entries() keeps yielding exactly these four for the other providers.
export const PROCESSING_STEPS: Record<Exclude<ProcessingStep, 'READING' | 'CONFIRMING'>, string> = {
  UPLOADING: 'Uploading documents to secure storage...',
  SUBMITTING: 'Submitting verification request...',
  PRE_PROCESSING: 'Preparing documents for verification...',
  PROCESSING: 'Verifying your documents...',
};

// ==================== STEP CONFIGURATION ====================

export type KycStep =
  | 'TYPE-DOC'
  | 'UPLOAD'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'ERROR'
  | 'END_PROCESSING'
  | 'IFRAME_VALIDATION'
  | 'RETRY_LIMIT_EXCEEDED'
  | 'BLOCKED' // PEP/Sanctions blocked (legacy)
  | 'REVIEW'; // Compliance/watchlist block routed to manual review (per-action copy)

export interface StepConfig {
  step: KycStep;
  title: string;
  icon: string;
  canGoBack: boolean;
  canGoNext: boolean;
  showCloseButton: boolean;
}

// ==================== PROVIDER CONFIGURATION ====================

export type KycProviderType =
  | 'textract'
  | 'thirdfort'
  | 'veriff'
  | 'verifflite'
  | 'somoskyc_v1'
  | 'mock';

export interface ProviderConfig {
  provider: KycProviderType;
  name: string;
  description: string;
  requiresIframe: boolean;
  requiresFileUpload: boolean;
  allowedDocumentTypes: DocumentTypeEnum[];
  privacyNotice: string;
}

// ==================== COMPONENT INPUT/OUTPUT ====================

export interface KycUploadDialogData {
  bucket: string;
  name: string;
  type: 'PERSONAL' | 'CLAIM';
  multiple: boolean;
  ref: string; // User ID
  provider: KycProviderType;
  action_id?: string;
  claim_id?: string; // Added for contact support navigation
  isComponentView?: boolean;
  notes?: any[]; // Adicionado para permitir passagem de notes (erros do backend)
  provider_return?: any; // Adicionado para permitir passagem de provider_return (erros do Veriff Lite)
  // Terminal verdict of a REOPENED check (cp_kyc_check.passed). Lets the reopen path apply the same
  // screen precedence as the polling path: passed===true → VERIFIED before any postpone/review check.
  passed?: boolean;
  // CPK-4807: opened from the outstanding-actions card on a postponed KYC — the postpone
  // screen offers a resubmit (retries left) or "Fechar" (retries spent) instead of "Prosseguir".
  reopenedFromOutstandingAction?: boolean;
  // Names mismatch classification data (for B2b/B3b error messages)
  mismatchCategory?: 'A' | 'B1' | 'B2' | 'B3' | 'C';
  mismatchSubCategory?: 'a' | 'b' | 'c';
}

export interface KycUploadResult {
  status: 'SUCCESS' | 'ERROR' | 'CANCELLED' | 'PENDING';
  checkId?: string;
  error?: KycError;
  data?: any;
}

// ==================== RETRY LOGIC ====================

export interface RetryConfig {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

// ==================== POLLING CONFIGURATION ====================

export interface PollingConfig {
  maxAttempts: number;
  initialIntervalMs: number;
  maxIntervalMs: number;
  backoffMultiplier: number;
  timeoutMs: number;
}

export const DEFAULT_POLLING_CONFIG: PollingConfig = {
  maxAttempts: 20, // 20 attempts
  initialIntervalMs: 2000, // Start with 2 seconds
  maxIntervalMs: 10000, // Max 10 seconds between attempts
  backoffMultiplier: 1.5, // Exponential backoff
  timeoutMs: 300000, // 5 minutes total timeout
};

// ==================== PREVIEW CONFIGURATION ====================

export interface PreviewConfig {
  showZoom: boolean;
  showRotate: boolean;
  maxPreviewWidth: number;
  maxPreviewHeight: number;
}

export const DEFAULT_PREVIEW_CONFIG: PreviewConfig = {
  showZoom: true,
  showRotate: true,
  maxPreviewWidth: 800,
  maxPreviewHeight: 600,
};
